import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowPathIcon,
  ChartBarIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';

const SupplierPerformance = ({ suppliers: propSuppliers, currentSupplierId }) => {
  const { getPurchases, getSuppliers } = useAppData();
  const [suppliers, setSuppliers] = useState(propSuppliers || []);
  const [performanceData, setPerformanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState('rating');
  const [sortDirection, setSortDirection] = useState('desc');

  // Charger et calculer les données de performance
  useEffect(() => {
    const calculatePerformanceData = async () => {
      setIsLoading(true);
      try {
        // Toujours récupérer les données fraîches depuis le contexte
        // pour assurer l'interconnexion avec les autres sections
        const allSuppliers = getSuppliers() || [];
        const allPurchases = getPurchases() || [];
        
        // Si aucun fournisseur n'est trouvé, utiliser les données du contexte
        let suppliersList = allSuppliers;
        
        // Si des fournisseurs spécifiques sont passés en props, les utiliser prioritairement
        if (propSuppliers && propSuppliers.length > 0) {
          // Mais s'assurer que leurs données sont à jour en fusionnant avec les données du contexte
          suppliersList = propSuppliers.map(propSupplier => {
            const contextSupplier = allSuppliers.find(s => s.id === propSupplier.id);
            return contextSupplier ? {...contextSupplier, ...propSupplier} : propSupplier;
          });
        }
        
        // Si nous avons un ID de fournisseur spécifique, filtrer la liste
        if (currentSupplierId) {
          suppliersList = suppliersList.filter(s => s.id === currentSupplierId);
          
          // Si le fournisseur n'est pas trouvé dans notre liste filtrée mais qu'on a un ID,
          // essayer de le récupérer directement depuis le contexte
          if (suppliersList.length === 0) {
            const specificSupplier = allSuppliers.find(s => s.id === currentSupplierId);
            if (specificSupplier) {
              suppliersList = [specificSupplier];
            }
          }
        }
        
        // Mettre à jour l'état des fournisseurs pour le composant
        setSuppliers(suppliersList);
        
        // Pour chaque fournisseur, calculer les métriques de performance
        const performance = suppliersList.map(supplier => {
          // Trouver les commandes associées à ce fournisseur
          const supplierPurchases = allPurchases.filter(p => 
            p.supplierId === supplier.id || 
            p.supplier === supplier.name ||
            (p.supplier && supplier.name && p.supplier.toLowerCase().includes(supplier.name.toLowerCase()))
          );
          
          // Calculer le délai de livraison moyen
          const deliveryTimes = supplierPurchases
            .filter(p => p.orderDate && p.deliveryDate)
            .map(p => {
              const orderDate = new Date(p.orderDate);
              const deliveryDate = new Date(p.deliveryDate);
              return Math.max(0, Math.ceil((deliveryDate - orderDate) / (1000 * 60 * 60 * 24)));
            });
          
          const avgDeliveryTime = deliveryTimes.length > 0
            ? deliveryTimes.reduce((sum, time) => sum + time, 0) / deliveryTimes.length
            : supplier.avgDeliveryTime || (supplier.deliveryTime ? parseFloat(supplier.deliveryTime) : 4);
          
          // Calculer le taux de livraison à temps
          const onTimeDeliveries = supplierPurchases.filter(p => {
            if (p.status === 'received' && p.expectedDate && p.deliveryDate) {
              const expectedDate = new Date(p.expectedDate);
              const deliveryDate = new Date(p.deliveryDate);
              return deliveryDate <= expectedDate;
            }
            return false;
          }).length;
          
          const totalRelevantPurchases = supplierPurchases.filter(p => 
            p.status === 'received' && p.expectedDate && p.deliveryDate
          ).length;
          
          const onTimeRate = totalRelevantPurchases > 0
            ? (onTimeDeliveries / totalRelevantPurchases) * 100
            : supplier.onTimeDeliveryRate || 85;
          
          // Récupérer ou calculer les métriques de qualité depuis les données réelles
          const completedPurchasesWithRating = supplierPurchases.filter(p => p.rating);
          const avgRating = completedPurchasesWithRating.length > 0
            ? completedPurchasesWithRating.reduce((sum, p) => sum + parseFloat(p.rating || 0), 0) / completedPurchasesWithRating.length
            : supplier.rating || 4.0;
          
          const quality = supplier.qualityRating || avgRating;
          const communication = supplier.communicationRating || avgRating;
          const priceCompetitiveness = supplier.priceCompetitiveness || avgRating;
          
          // Calculer la note globale
          const overallRating = ((quality + communication + priceCompetitiveness) / 3).toFixed(1);
          
          return {
            ...supplier,
            rating: parseFloat(overallRating),
            avgDeliveryTime: parseFloat(avgDeliveryTime.toFixed(1)),
            onTimeDeliveryRate: Math.round(onTimeRate),
            orders: supplierPurchases.length || supplier.orders || 0,
            qualityRating: quality,
            communicationRating: communication,
            priceCompetitiveness: priceCompetitiveness,
            lastOrder: (supplierPurchases.length > 0 ? 
              supplierPurchases.sort((a, b) => new Date(b.date || b.orderDate || 0) - new Date(a.date || a.orderDate || 0))[0].date ||
              supplierPurchases.sort((a, b) => new Date(b.date || b.orderDate || 0) - new Date(a.date || a.orderDate || 0))[0].orderDate :
              supplier.lastOrder || null)
          };
        });
        
        // Sauvegarder les données de performance
        setPerformanceData(performance);
        console.log('Données de performance calculées:', performance.length);
        
        // Mettre à jour le localStorage avec les données enrichies
        if (performance.length > 0) {
          const allSuppliersInStorage = JSON.parse(localStorage.getItem('stockpro_suppliers')) || [];
          
          // Mettre à jour uniquement les fournisseurs pour lesquels nous avons calculé des performances
          const updatedSuppliers = allSuppliersInStorage.map(s => {
            const updatedSupplier = performance.find(p => p.id === s.id);
            return updatedSupplier || s;
          });
          
          localStorage.setItem('stockpro_suppliers', JSON.stringify(updatedSuppliers));
        }
        
      } catch (error) {
        console.error('Erreur lors du calcul des performances:', error);
        toast.error('Erreur lors du calcul des performances');
      } finally {
        setIsLoading(false);
      }
    };
    
    calculatePerformanceData();
    // Inclure suppliers dans la dépendance array pour garantir que les calculs
    // sont mis à jour lorsque les données des fournisseurs changent
  }, [getSuppliers, getPurchases, currentSupplierId, propSuppliers]);
  
  // Trier les données de performance
  const getSortedData = () => {
    return [...performanceData].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'rating') {
        comparison = b.rating - a.rating; // Note décroissante par défaut
      } else if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'onTimeDeliveryRate') {
        comparison = b.onTimeDeliveryRate - a.onTimeDeliveryRate;
      } else if (sortField === 'avgDeliveryTime') {
        comparison = a.avgDeliveryTime - b.avgDeliveryTime; // Temps de livraison croissant
      } else if (sortField === 'orders') {
        comparison = b.orders - a.orders;
      }
      
      return sortDirection === 'asc' ? -comparison : comparison;
    });
  };

  // Changer le tri
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'avgDeliveryTime' ? 'asc' : 'desc'); // Par défaut, tri croissant pour le délai
    }
  };
  
  // Export function removed
  
  // Actualiser les données en forçant un recalcul complet
  const handleRefresh = () => {
    // Reset the data
    setPerformanceData([]);
    setIsLoading(true);
    
    // Fetch fresh data from context
    const allSuppliers = getSuppliers() || [];
    const allPurchases = getPurchases() || [];
    
    // Determine which suppliers to show
    let refreshedSuppliers = allSuppliers;
    
    // If specific suppliers were provided or there's a specific ID
    if (propSuppliers && propSuppliers.length > 0) {
      refreshedSuppliers = propSuppliers.map(propSupplier => {
        const contextSupplier = allSuppliers.find(s => s.id === propSupplier.id);
        return contextSupplier ? {...contextSupplier, ...propSupplier} : propSupplier;
      });
    }
    
    if (currentSupplierId) {
      refreshedSuppliers = refreshedSuppliers.filter(s => s.id === currentSupplierId);
      if (refreshedSuppliers.length === 0) {
        const specificSupplier = allSuppliers.find(s => s.id === currentSupplierId);
        if (specificSupplier) {
          refreshedSuppliers = [specificSupplier];
        }
      }
    }
    
    // Update suppliers state to trigger useEffect and recalculation
    setSuppliers(refreshedSuppliers);
    toast.success('Données de performance actualisées');
  };

  // Rendu des étoiles pour la notation
  const renderStarRating = (rating) => {
    // Arrondir à 0.5 près
    const roundedRating = Math.round(rating * 2) / 2;
    const fullStars = Math.floor(roundedRating);
    const hasHalfStar = roundedRating % 1 !== 0;
    
    return (
      <div className="flex items-center justify-center">
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i} 
            className={`h-5 w-5 ${
              i < fullStars 
                ? 'text-amber-400' 
                : i === fullStars && hasHalfStar 
                  ? 'text-amber-400' 
                  : 'text-white/30'
            }`}
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            {i === fullStars && hasHalfStar ? (
              <defs>
                <linearGradient id={`halfStar${i}`}>
                  <stop offset="50%" stopColor="currentColor" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                </linearGradient>
              </defs>
            ) : null}
            <path 
              fill={i === fullStars && hasHalfStar ? `url(#halfStar${i})` : 'currentColor'}
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" 
            />
          </svg>
        ))}
        <span className="ml-2 text-white">{rating}</span>
      </div>
    );
  };

  // Obtenir la couleur en fonction de la valeur
  const getColorClass = (value, thresholds = { high: 90, medium: 70 }, inverse = false) => {
    if (inverse) {
      if (value <= thresholds.high) return 'text-green-400';
      if (value <= thresholds.medium) return 'text-amber-400';
      return 'text-red-400';
    } else {
      if (value >= thresholds.high) return 'text-green-400';
      if (value >= thresholds.medium) return 'text-amber-400';
      return 'text-red-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Performance des fournisseurs</h2>
            <p className="text-white/60 text-sm mt-1">
              Analyse complète des performances et métriques de qualité
            </p>
          </div>
          {/* Export button removed */}
        </div>
      </div>
      
      {/* Tableau des performances */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th 
                  className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    <span>Fournisseur</span>
                    {sortField === 'name' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('rating')}
                >
                  <div className="flex items-center justify-center">
                    <span>Note globale</span>
                    {sortField === 'rating' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('onTimeDeliveryRate')}
                >
                  <div className="flex items-center justify-center">
                    <span>Livraison à temps</span>
                    {sortField === 'onTimeDeliveryRate' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('avgDeliveryTime')}
                >
                  <div className="flex items-center justify-center">
                    <span>Délai moyen</span>
                    {sortField === 'avgDeliveryTime' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('orders')}
                >
                  <div className="flex items-center justify-center">
                    <span>Commandes</span>
                    {sortField === 'orders' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                    <p className="mt-2 text-white/70">Calcul des performances...</p>
                  </td>
                </tr>
              ) : getSortedData().length > 0 ? (
                getSortedData().map((supplier, index) => (
                  <motion.tr 
                    key={supplier.id} 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-white">{supplier.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {renderStarRating(supplier.rating)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-24 bg-white/10 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              supplier.onTimeDeliveryRate >= 90 ? 'bg-green-500' :
                              supplier.onTimeDeliveryRate >= 80 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${supplier.onTimeDeliveryRate}%` }}
                          ></div>
                        </div>
                        <span className={`ml-2 text-sm ${getColorClass(supplier.onTimeDeliveryRate)}`}>
                          {supplier.onTimeDeliveryRate}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm ${getColorClass(supplier.avgDeliveryTime, { high: 3, medium: 7 }, true)}`}>
                        {supplier.avgDeliveryTime} jours
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                      {supplier.orders}
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <ExclamationCircleIcon className="h-10 w-10 text-white/40 mb-2" />
                      <p className="text-white/60">Aucune donnée de performance disponible</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Détail des métriques */}
      {getSortedData().length > 0 && !currentSupplierId && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Performance globale des fournisseurs</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Note moyenne</h4>
              <div className="flex items-end">
                <span className="text-2xl font-semibold text-white">
                  {(getSortedData().reduce((sum, item) => sum + item.rating, 0) / getSortedData().length).toFixed(1)}
                </span>
                <span className="ml-2 text-white/60">/ 5</span>
              </div>
              <div className="mt-2 w-full bg-white/10 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-blue-500"
                  style={{ 
                    width: `${(getSortedData().reduce((sum, item) => sum + item.rating, 0) / getSortedData().length / 5) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Livraison à temps</h4>
              <div className="flex items-end">
                <span className={`text-2xl font-semibold ${
                  getColorClass(getSortedData().reduce((sum, item) => sum + item.onTimeDeliveryRate, 0) / getSortedData().length)
                }`}>
                  {Math.round(getSortedData().reduce((sum, item) => sum + item.onTimeDeliveryRate, 0) / getSortedData().length)}%
                </span>
              </div>
              <div className="mt-2 w-full bg-white/10 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    getSortedData().reduce((sum, item) => sum + item.onTimeDeliveryRate, 0) / getSortedData().length >= 90 
                      ? 'bg-green-500' 
                      : getSortedData().reduce((sum, item) => sum + item.onTimeDeliveryRate, 0) / getSortedData().length >= 80
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${getSortedData().reduce((sum, item) => sum + item.onTimeDeliveryRate, 0) / getSortedData().length}%` 
                  }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Délai moyen</h4>
              <div className="flex items-end">
                <span className={`text-2xl font-semibold ${
                  getColorClass(
                    getSortedData().reduce((sum, item) => sum + item.avgDeliveryTime, 0) / getSortedData().length,
                    { high: 3, medium: 7 },
                    true
                  )
                }`}>
                  {(getSortedData().reduce((sum, item) => sum + item.avgDeliveryTime, 0) / getSortedData().length).toFixed(1)}
                </span>
                <span className="ml-2 text-white/60">jours</span>
              </div>
              <div className="mt-2 w-full bg-white/10 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    getSortedData().reduce((sum, item) => sum + item.avgDeliveryTime, 0) / getSortedData().length <= 3
                      ? 'bg-green-500'
                      : getSortedData().reduce((sum, item) => sum + item.avgDeliveryTime, 0) / getSortedData().length <= 7
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (getSortedData().reduce((sum, item) => sum + item.avgDeliveryTime, 0) / getSortedData().length / 10) * 100)}%` 
                  }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Total commandes</h4>
              <div className="flex items-end">
                <span className="text-2xl font-semibold text-white">
                  {getSortedData().reduce((sum, item) => sum + item.orders, 0)}
                </span>
              </div>
              <div className="mt-2 text-xs text-white/60">
                {getSortedData().length} fournisseurs actifs
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-sm font-medium text-white/70 mb-3">Meilleurs fournisseurs par note</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getSortedData().slice(0, 3).map((supplier, index) => (
                <div key={supplier.id} className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div className="ml-4">
                    <div className="text-white font-medium">{supplier.name}</div>
                    <div className="flex items-center mt-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <svg 
                            key={i} 
                            className={`h-4 w-4 ${i < Math.round(supplier.rating) ? 'text-amber-400' : 'text-white/30'}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-1 text-white/70 text-xs">{supplier.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Métriques détaillées pour un fournisseur spécifique */}
      {getSortedData().length === 1 && currentSupplierId && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-6">Métriques détaillées</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-white/70 text-sm mb-4">Évaluation qualité</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-white">Qualité des produits</span>
                    <span className="text-white">{getSortedData()[0].qualityRating}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-green-500"
                      style={{ width: `${(getSortedData()[0].qualityRating / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-white">Communication</span>
                    <span className="text-white">{getSortedData()[0].communicationRating}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${(getSortedData()[0].communicationRating / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-white">Compétitivité des prix</span>
                    <span className="text-white">{getSortedData()[0].priceCompetitiveness}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-purple-500"
                      style={{ width: `${(getSortedData()[0].priceCompetitiveness / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white/70 text-sm mb-4">Livraison</h4>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-semibold text-white">{getSortedData()[0].onTimeDeliveryRate}%</div>
                  <div className="text-white/60 text-sm mt-1">Taux de livraison à temps</div>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-semibold text-white">{getSortedData()[0].avgDeliveryTime}</div>
                  <div className="text-white/60 text-sm mt-1">Jours de délai moyen</div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white/70 text-sm mb-4">Commandes</h4>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-3xl font-semibold text-white">{getSortedData()[0].orders}</div>
                  <div className="text-white/60 text-sm mt-1">Nombre total de commandes</div>
                </div>
                
                {getSortedData()[0].lastOrder && (
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-white font-semibold">
                      {new Date(getSortedData()[0].lastOrder).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="text-white/60 text-sm mt-1">Dernière commande</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPerformance;